/**
 * The three "tiny text" styles of the Small Text Generator: raised letters, small
 * capitals and lowered letters. Every mapping is a one-to-one Unicode lookalike, so a
 * letter without a small form is left alone rather than swapped for a wrong one.
 *
 * Code points were checked against the Unicode Character Database (UnicodeData and
 * DerivedAge, Unicode 18). The "newest" set holds the letters that exist in Unicode but
 * are missing from most fonts:
 *
 * - U+107A5 MODIFIER LETTER SMALL Q (Unicode 14)
 * - U+A7F2 / U+A7F3 / U+A7F4 MODIFIER LETTER CAPITAL C / F / Q (Unicode 14)
 * - U+A7F1 MODIFIER LETTER CAPITAL S (Unicode 17)
 * - U+A7AF LATIN LETTER SMALL CAPITAL Q (Unicode 11)
 *
 * Modifier capital X, Y and Z, and a small-capital X, do not exist in any Unicode
 * version, so those letters always stay as typed.
 */

function pairs(source: string, target: string): Map<string, string> {
  const map = new Map<string, string>();
  const from = [...source];
  const to = [...target];
  for (const [index, char] of from.entries()) {
    map.set(char, to[index]);
  }
  return map;
}

const SUPERSCRIPT_BASE = pairs(
  "abcdefghijklmnoprstuvwxyzABDEGHIJKLMNOPRTUVW0123456789",
  "ᵃᵇᶜᵈᵉᶠᵍʰⁱʲᵏˡᵐⁿᵒᵖʳˢᵗᵘᵛʷˣʸᶻᴬᴮᴰᴱᴳᴴᴵᴶᴷᴸᴹᴺᴼᴾᴿᵀᵁⱽᵂ⁰¹²³⁴⁵⁶⁷⁸⁹"
);

/** Raised letters added in Unicode 14 and 17; absent from most fonts. */
const SUPERSCRIPT_NEWEST = pairs("qCFQS", "\u{107A5}ꟲꟳꟴ꟱");

/** Small capitals A–Z except X (which has none). Q is the lookalike ǫ. */
const SMALL_CAPS_BASE = pairs(
  "abcdefghijklmnopqrstuvwyz",
  "ᴀʙᴄᴅᴇꜰɢʜɪᴊᴋʟᴍɴᴏᴘǫʀꜱᴛᴜᴠᴡʏᴢ"
);

/** The true small capital Q, LATIN LETTER SMALL CAPITAL Q (Unicode 11). */
const SMALL_CAPS_TRUE_Q = "ꞯ";

const SUBSCRIPT_BASE = pairs(
  "aehijklmnoprstuvx0123456789",
  "ₐₑₕᵢⱼₖₗₘₙₒₚᵣₛₜᵤᵥₓ₀₁₂₃₄₅₆₇₈₉"
);

export interface SmallTextOptions {
  /** Use the newest Unicode letters (raised q C F Q S, small-cap Q) even though many fonts lack them. */
  newest?: boolean;
}

export function toSuperscript(
  text: string,
  { newest = false }: SmallTextOptions = {}
): string {
  return [...text]
    .map(
      (char) =>
        (newest ? SUPERSCRIPT_NEWEST.get(char) : undefined) ??
        SUPERSCRIPT_BASE.get(char) ??
        char
    )
    .join("");
}

export function toSmallCaps(
  text: string,
  { newest = false }: SmallTextOptions = {}
): string {
  return [...text]
    .map((char) => {
      const lower = char.toLowerCase();
      if (newest && lower === "q") {
        return SMALL_CAPS_TRUE_Q;
      }
      return SMALL_CAPS_BASE.get(lower) ?? char;
    })
    .join("");
}

export function toSubscript(text: string): string {
  return [...text]
    .map((char) => SUBSCRIPT_BASE.get(char.toLowerCase()) ?? char)
    .join("");
}
